import { ApplicationConfig, provideZoneChangeDetection, ErrorHandler } from '@angular/core';
import { provideRouter, TitleStrategy } from '@angular/router';

import { routes } from './app.routes';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { InterceptorService, ThemeService, GlobalErrorHandler } from './services';
import { SeoTitleStrategy } from './services/seo.service';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }), 
    provideRouter(routes), 
    // SSR was removed: this is a CSR-only build (no main.server.ts / server.ts, no @angular/ssr,
    // no server or prerender target in angular.json) and index.html ships a bare <app-root>.
    // provideClientHydration() therefore had no serialized state to hydrate from — withEventReplay()
    // and the transfer-cache options were permanent no-ops that only shipped bytes and, in dev,
    // produced the NG0505 "hydration requested but no serialized information present" warning.
    provideHttpClient(
      withFetch(), 
      withInterceptorsFromDi(),
    ), 
    { provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true }, 
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Per-route <title>; defers to SeoService on routes that declare none (see SeoTitleStrategy).
    { provide: TitleStrategy, useClass: SeoTitleStrategy },
    // WHY: the CDK marks every sibling of the overlay aria-hidden="true" whenever a
    // dialog opens (cdk/dialog: _hideNonDialogContentFromAssistiveTechnology), but
    // MatDialogConfig.ariaModal defaults to false. The pair told a screen reader the
    // page was still in play while removing app-root from the accessibility tree.
    // Set here rather than per-call so every dialog in the app is covered; per-call
    // configs still win, since MatDialog.open() spreads the defaults underneath them.
    //
    // It must be a real MatDialogConfig, not a bare { ariaModal: true }: open() does
    // `{...(this._defaultOptions || new MatDialogConfig()), ...config}` — providing
    // this token REPLACES the class defaults rather than extending them, so a bare
    // object would silently drop role='dialog', hasBackdrop, autoFocus and
    // restoreFocus from every dialog in the app.
    // maxWidth/maxHeight: keep every dialog usable on small screens. Material's default maxWidth is 80vw,
    // which made dialogs without their own maxWidth only 256px wide at 320px. This gives up to 95vw on
    // phones (min(95vw, 600px)) and stays exactly 80vw from ~750px up, so desktop is unchanged.
    // Per-call values still win.
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: Object.assign(new MatDialogConfig(), { ariaModal: true, maxWidth: 'max(80vw, min(95vw, 600px))', maxHeight: '90dvh' }) },
    ThemeService,
  ],
};
